import { AuthStatus, Role, UserStatus, DbRef } from '@caresend/types';
import { hasStatus } from '@caresend/utils';
import dayjs from 'dayjs';
import { initializeEncryptionAccess, deinitializeEncryptionAccess } from '../../../database/encryption/init.js';
import 'firebase/auth';
import '../../../database/firebaseConfig/cloudFunctions.js';
import { firebaseAuth } from '../../../database/firebaseConfig/init.js';
import { firebaseBind, firebaseUnbind } from '../../../database/firebaseConfig/methods.js';
import { setSentryUser } from '../../../functions/sentry.js';
import '@fortawesome/fontawesome-svg-core';
import '@fortawesome/sharp-regular-svg-icons';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import { getStore } from '../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { isProUser } from '../../helpers.js';
import { initExtendedModule } from '../helpers.js';

const authState = {
    authLoading: true,
    authStatus: AuthStatus.SIGNED_OUT,
    encryptionAccess: false,
    user: undefined,
};
const authMutations = {
    'auth/SET_AUTH_LOADING': (state, loading) => {
        state.authLoading = loading;
    },
    'auth/SET_AUTH_STATUS': (state, payload) => {
        state.authStatus = payload;
    },
    'auth/SET_USER': (state, payload) => {
        state.user = payload;
    },
    'auth/SET_ENCRYPTION_ACCESS': (state, payload) => {
        state.encryptionAccess = payload;
    },
};
const authActions = {
    'auth/bindUser': async ({ commit }, authUser) => {
        var _a;
        commit('auth/SET_AUTH_STATUS', AuthStatus.FIREBASE_AUTH);
        commit('auth/SET_AUTH_LOADING', true);
        const { uid: userID } = authUser;
        setSentryUser({
            id: authUser.uid,
            email: (_a = authUser.email) !== null && _a !== void 0 ? _a : '',
        });
        const path = `${DbRef.USERS}/${userID}`;
        await firebaseBind(path, async (user) => {
            commit('auth/SET_USER', user);
        }, { preventAutoUnbind: true });
    },
    'auth/unbindUser': async ({ commit }, userID) => {
        await firebaseUnbind(`${DbRef.USERS}/${userID}`, { force: true });
        commit('auth/SET_USER', undefined);
        commit('auth/SET_AUTH_STATUS', AuthStatus.SIGNED_OUT);
        commit('auth/SET_AUTH_LOADING', false);
    },
};
const authGetters = {
    'auth/getUser': (state) => state.user,
    'auth/getUserStatus': (state) => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.status; },
    'auth/getUserRole': (state) => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.role; },
    'auth/getUserInfo': (state) => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.info; },
    'auth/getUserName': (state) => {
        var _a;
        if ((_a = state.user) === null || _a === void 0 ? void 0 : _a.info) {
            return `${state.user.info.firstName} ${state.user.info.lastName}`;
        }
        return null;
    },
    'auth/getUserMapLocation': (state) => { var _a, _b; return (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.current; },
    'auth/getUserID': (state) => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.id; },
    'auth/getUserTimeZone': (state) => { var _a, _b, _c; return (_c = (_b = (_a = state.user) === null || _a === void 0 ? void 0 : _a.info.address) === null || _b === void 0 ? void 0 : _b.timeZone) !== null && _c !== void 0 ? _c : dayjs.tz.guess(); },
    'auth/getUserLocation': (state) => { var _a; return (_a = state.user) === null || _a === void 0 ? void 0 : _a.location; },
    'auth/getAuthStatus': (state) => state.authStatus,
};
/**
 * For pro users only.
 *
 * Only bind office on user update if the update was consistent with the user
 * clicking "Join office" after being invited.
 *
 * The user's status may not be changed to approved at the same time as offices
 * are added to the user, so returns true once both conditions are met.
 */
const shouldBindOfficeOnUserUpdate = (prevUser, user) => {
    const hadOffice = !!(prevUser === null || prevUser === void 0 ? void 0 : prevUser.offices);
    const hasOffice = !!(user === null || user === void 0 ? void 0 : user.offices);
    const officeAdded = !hadOffice && hasOffice;
    const wasApproved = hasStatus(prevUser === null || prevUser === void 0 ? void 0 : prevUser.status, UserStatus.APPROVED);
    const isApproved = hasStatus(user === null || user === void 0 ? void 0 : user.status, UserStatus.APPROVED);
    const becameApproved = !wasApproved && isApproved;
    if ((officeAdded && isApproved)
        || (hasOffice && becameApproved))
        return true;
    return false;
};
const maybeBindOrUnbindOffice = async (change, prevUser, user) => {
    var _a, _b;
    const store = getStore();
    const relevantUser = change === 'userUnbound' ? prevUser : user;
    const firstOfficeID = (_b = (_a = relevantUser === null || relevantUser === void 0 ? void 0 : relevantUser.offices) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.id;
    if (change === 'userUnbound') {
        if (store.state.office && firstOfficeID) {
            await store.dispatch('office/unbindOffice', firstOfficeID);
        }
        return;
    }
    // Only check for pro user after unbound action to support unbinding office
    // when app switched in Storybook.
    if (!isProUser() || !(user === null || user === void 0 ? void 0 : user.offices))
        return;
    if (change === 'userUpdated'
        && prevUser
        && !shouldBindOfficeOnUserUpdate(prevUser, user))
        return;
    if (!firstOfficeID)
        throw Error('Missing office');
    await store.dispatch('office/bindOffice', firstOfficeID);
};
const initAuthWatches = ({ handleUserBound, handleUserObjectUpdated, handleUserUnbound } = {}) => {
    // Store not passed in for typechecking, as it is expected all apps will
    // have the auth module.
    const store = getStore();
    // Perform actions when user logs in and out, after the user object
    // has been updated in the store.
    store.watch((state) => state.auth.user, async (user, prevUser) => {
        const userBound = !!user && !prevUser;
        const userUpdated = !!user && !!prevUser;
        const userUnbound = !user && !!prevUser;
        if (userBound) {
            await maybeBindOrUnbindOffice('userBound', prevUser, user);
            await (handleUserBound === null || handleUserBound === void 0 ? void 0 : handleUserBound(store, user));
        }
        else if (userUpdated) {
            await maybeBindOrUnbindOffice('userUpdated', prevUser, user);
            await (handleUserObjectUpdated === null || handleUserObjectUpdated === void 0 ? void 0 : handleUserObjectUpdated(store, user, prevUser));
        }
        else if (userUnbound) {
            await maybeBindOrUnbindOffice('userUnbound', prevUser, user);
            await (handleUserUnbound === null || handleUserUnbound === void 0 ? void 0 : handleUserUnbound(store));
        }
    }, { immediate: true, deep: true });
    /**
     * Once the user is bound, the office is potentially bound, and encryption
     * access has been initialized, the auth status is set to complete.
     */
    store.watch((state) => {
        var _a;
        return [
            state.auth.user,
            (_a = state.office) === null || _a === void 0 ? void 0 : _a.office,
            state.auth.encryptionAccess,
        ];
    }, () => {
        var _a;
        const { user, encryptionAccess } = store.state.auth;
        const office = (_a = store.state.office) === null || _a === void 0 ? void 0 : _a.office;
        if (((user === null || user === void 0 ? void 0 : user.role) === Role.PRESCRIBER
            || (user === null || user === void 0 ? void 0 : user.role) === Role.ASSISTANT)) {
            if (!office || !encryptionAccess) {
                return store.commit('auth/SET_AUTH_STATUS', AuthStatus.FIREBASE_AUTH);
            }
            return store.commit('auth/SET_AUTH_STATUS', AuthStatus.COMPLETE);
        }
        if (user && encryptionAccess) {
            return store.commit('auth/SET_AUTH_STATUS', AuthStatus.COMPLETE);
        }
    }, { immediate: true, deep: true });
};
/**
 * Initialize callbacks for when the firebase auth state changes. These take
 * place immediately when the firebase auth state changes, before the user
 * object is updated in the store. If actions need to take place using the user
 * object, use `handleUserBound` in `initAuthWatches`.
 */
const initFirebaseAuthWatch = ({ handleUserAuthed, handleUserLoggedOut, handleAuthInitializedWithoutUser } = {}) => {
    firebaseAuth.onAuthStateChanged((authUser) => {
        // Store not passed in for typechecking, as it is expected all apps will
        // have the auth module.
        const store = getStore();
        if (authUser !== null) {
            store.dispatch('auth/bindUser', authUser);
            initializeEncryptionAccess();
            handleUserAuthed === null || handleUserAuthed === void 0 ? void 0 : handleUserAuthed(authUser);
        }
        else if (store.state.auth.user !== undefined) {
            const loggedOutUserID = store.state.auth.user.id;
            store.dispatch('auth/unbindUser', loggedOutUserID);
            deinitializeEncryptionAccess();
            handleUserLoggedOut === null || handleUserLoggedOut === void 0 ? void 0 : handleUserLoggedOut();
        }
        else {
            store.commit('auth/SET_AUTH_LOADING', false);
            handleAuthInitializedWithoutUser === null || handleAuthInitializedWithoutUser === void 0 ? void 0 : handleAuthInitializedWithoutUser();
        }
    });
};
const authModule = {
    state: authState,
    mutations: authMutations,
    actions: authActions,
    getters: authGetters,
};
const initAuthModule = (moduleExtensions) => initExtendedModule(authModule, moduleExtensions);

export { initAuthModule, initAuthWatches, initFirebaseAuthWatch };
